import { useCallback, useState } from "react";
import { Button } from "@/components/ui/button";
import { ImagePlus, X } from "lucide-react";

interface ImageUploadProps {
  value: string | File;
  onChange: (value: string | File) => void;
  onRemove: () => void;
}

export function ImageUpload({
  value,
  onChange,
  onRemove
}: ImageUploadProps) {
  const [isLoading, setIsLoading] = useState(false);

  const handleUpload = useCallback(async (file: File) => {
    try {
      setIsLoading(true);
      
      // FormData oluştur
      const formData = new FormData();
      formData.append('file', file);

      console.log('Dosya yükleme başlatıldı:', file.name);
      
      // Dosyayı yükle
      const response = await fetch(`${import.meta.env.VITE_API_URL}/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Dosya yükleme hatası:', response.status, errorText);
        throw new Error(`Dosya yüklenemedi: ${response.status} ${errorText}`);
      }

      const data = await response.json();
      console.log('Dosya yükleme başarılı:', data);

      // File nesnesini direkt olarak onChange'e geçir
      onChange(file);
      
    } catch (error) {
      console.error('Dosya yükleme hatası:', error);
      // Hata durumunda boş string döndür
      onChange('');
    } finally {
      setIsLoading(false);
    }
  }, [onChange]);

  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleUpload(file);
    }
  }, [handleUpload]);

  return (
    <div className="flex items-center gap-4">
      <div className="relative w-40 h-40 rounded-lg border-2 border-dashed flex items-center justify-center">
        {value ? (
          <>
            {typeof value === 'string' ? (
              <img
                src={value}
                alt="Uploaded"
                className="w-full h-full object-cover rounded-lg"
              />
            ) : (
              <img
                src={URL.createObjectURL(value)}
                alt="Uploaded"
                className="w-full h-full object-cover rounded-lg"
              />
            )}
            <button
              onClick={onRemove}
              className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full p-1 shadow-sm"
              type="button"
            >
              <X className="h-4 w-4" />
            </button>
          </>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <ImagePlus className="h-10 w-10 text-muted-foreground" />
            <p className="text-xs text-muted-foreground">
              PNG, JPG veya GIF
            </p>
          </div>
        )}
        <input
          type="file"
          accept="image/*"
          className="absolute inset-0 opacity-0 cursor-pointer"
          onChange={handleFileSelect}
          disabled={isLoading}
        />
      </div>
      {!value && (
        <Button
          type="button"
          variant="outline"
          disabled={isLoading}
          className="ml-4"
          onClick={() => {
            const fileInput = document.querySelector('input[type="file"]');
            if (fileInput instanceof HTMLInputElement) {
              fileInput.click();
            }
          }}
        >
          {isLoading ? 'Yükleniyor...' : 'Görsel Seç'}
        </Button>
      )}
    </div>
  );
}
